const cdmMap = new Map(); // satelliteId -> array of conjunction events involving it

/**
 * Groups mirrored CDM pairs (same event reported from both objects'
 * perspectives) into one canonical dedup key, so display code doesn't
 * show the same real-world event twice.
 */
function dedupKey(rec) {
  const ids = [String(rec.SAT_1_ID), String(rec.SAT_2_ID)].sort();
  return `${rec.TCA}|${ids[0]}|${ids[1]}`;
}

export async function loadCDMs(url = '/data/cdm_data.json') {
  try {
    const res = await fetch(url);
    const json = await res.json();
    const records = json?.data || [];

    cdmMap.clear();
    const seenDedupKeys = new Map(); // dedupKey -> canonical record (first one seen)

    for (const rec of records) {
      const key = dedupKey(rec);
      if (!seenDedupKeys.has(key)) {
        seenDedupKeys.set(key, rec);
      } else {
        // If this mirrored copy has a PC value and the stored one doesn't,
        // prefer the one with real data rather than silently dropping it.
        const existing = seenDedupKeys.get(key);
        if (existing.PC == null && rec.PC != null) {
          seenDedupKeys.set(key, rec);
        }
      }
    }

    const dedupedRecords = Array.from(seenDedupKeys.values());

    for (const rec of dedupedRecords) {
      const sat1 = String(rec.SAT_1_ID);
      const sat2 = String(rec.SAT_2_ID);

      const event = {
        cdmId: rec.CDM_ID,
        tca: rec.TCA,
        minRangeM: rec.MIN_RNG != null ? Number(rec.MIN_RNG) : null,
        pc: rec.PC != null ? Number(rec.PC) : null,
        emergencyReportable: rec.EMERGENCY_REPORTABLE === 'Y',
      };

      for (const [selfId, otherId, otherName, otherType] of [
        [sat1, sat2, rec.SAT_2_NAME, rec.SAT2_OBJECT_TYPE],
        [sat2, sat1, rec.SAT_1_NAME, rec.SAT1_OBJECT_TYPE],
      ]) {
        if (!cdmMap.has(selfId)) cdmMap.set(selfId, []);
        cdmMap.get(selfId).push({
          ...event,
          otherSatelliteId: otherId,
          otherSatelliteName: otherName,
          otherObjectType: otherType,
        });
      }
    }

    console.log(`[CDM] Indexed ${dedupedRecords.length} unique events across ${cdmMap.size} satellites.`);
  } catch (err) {
    console.error('[CDM] Failed to load CDM data:', err);
  }
}

export function getConjunctionsForSatellite(satelliteId) {
  return cdmMap.get(String(satelliteId)) || [];
}

export function hasConjunctions(satelliteId) {
  return cdmMap.has(String(satelliteId));
}
export function getAllSatelliteIdsWithConjunctions() {
  return new Set(cdmMap.keys());
}